import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IFormPartnerBasicInfo } from 'legacy/lut/classes/models/partner360/IFormPartnerBasicInfo'
import { IWWPartners } from 'legacy/lut/classes/models/partner360/IWWPartners'
import { appGetState } from 'legacy/lut/store'
import sanitizeHtml from 'sanitize-html'

import { downloadBlob } from 'clickNgo/helpers/common'
import { NotificationController } from 'core-components/library/popup'
import { IFilter, Logic, Operator } from 'core-components/types'
import { guid } from 'core/classes/helpers/GuidHelper'
import { saveFile } from 'core/classes/helpers/saveFiles'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import settings from 'src/config/settings'

import { IFormJBPReportType } from '../../models/partner360/IFormJBPReportType'
import { ProgramTypes } from '../../models/ProgramTypes'
import { Partner360Api } from '../api/partner360/Partner360Api'

export type TPartnerPlanExportFormats = 'PDF' | 'PPTX' | 'JSON'

/** Layout of the backend PDF: 1 is the full legacy plan, 2 the compact FY27 one. */
export type TPartnerPlanPdfVersion = 1 | 2

/** Every PDF request names its layout, so the result never depends on the service's own
 *  per-environment default. */
export const PARTNER_PLAN_PDF_VERSION_DEFAULT: TPartnerPlanPdfVersion = 1

interface IExportPartnerPlanViaService {
  pid: string
  fYear: number
  exportFormat: TPartnerPlanExportFormats
  programType?: string
  openResultInNewTab?: boolean
  /** PDF only; defaults to `PARTNER_PLAN_PDF_VERSION_DEFAULT`. */
  pdfVersion?: TPartnerPlanPdfVersion
}

interface IGeneratePdfPartnerPlanViaAppDes {
  pid: string
  fYear: number
  programType?: string
  preventDownload: boolean
}

interface IKeysFromJSON {
  keys: string[] | IObjectAny[]
  pid?: string
  fyear?: number
  programType?: string
  partnerType?: string
  partnerRank?: number
  partnerRanks?: string
  pbmEmail?: string
  managerEmail?: string
  category?: string
  subCategory?: string
  opsPartner?: number
  bpsPartner?: number
  strategicMPSPartner?: number
  strategicPrintServicesPartner?: number
  strategicPSServicesPartner?: number
  polyPartner?: number
  amplifyImpact?: number
  market?: string
  country?: string
  guid?: string
}

interface IGenerateReport {
  reportType: IFormJBPReportType
  pid?: string
  fyear?: number
  programType?: string
  market?: string
  country?: string
  category?: string
  subCategory?: string
  partnerType?: string
  partnerRank?: number
  partnerRanks?: string
  pbmEmail?: string
  managerEmail?: string
  opsPartner?: number
  bpsPartner?: number
  strategicMPSPartner?: number
  strategicPrintServicesPartner?: number
  strategicPSServicesPartner?: number
  polyPartner?: number
  amplifyImpact?: number
  saveResultToAttachmentForm?: boolean
  scope?: string
  products?: string[]
  globalPartnerName?: IWWPartners[]
  guid?: string
}

export enum ExportMappingTypes {
  ExportBPMPSMappings = 'ExportBPMPSMappings',
  ExportBPDaaSMappings = 'ExportBPDaaSMappings',
  ExportBPGeneralMappings = 'ExportBPGeneralMappings',
  ExportPartner360AllMappings = 'ExportPartner360AllMappings',
  ExportPartner360Completeness = 'ExportPartner360Completeness',
  ExportMasterDealer = 'ExportMasterDealer',
  ExportPartner360CompletenessPivot = 'ExportPartner360CompletenessPivot',
  ExportMDFPlan = 'ExportMDFPlan',
  ExportPartner360TargetsComparison = 'ExportPartner360TargetsComparison',
  ExportMasterDealerReport = 'ExportMasterDealerReport',
}

const getFilename = (
  type: ExportMappingTypes,
  pid?: string,
  fyear?: number,
  ProgramType?: string
) => {
  switch (type) {
    case 'ExportPartner360Completeness':
      return 'completeness report.xlsx'
    case 'ExportPartner360CompletenessPivot':
      return 'completeness report pivot.xlsx'
    case 'ExportPartner360AllMappings':
      return !fyear ? 'partner 360.xlsx' : `partner 360 ${fyear}.xlsx`
    case `ExportMasterDealer`:
      return `${pid} master dealer.xlsx`
    case 'ExportMDFPlan':
      return !fyear
        ? `${pid} actions plan ${ProgramType}.xlsx`
        : `${pid} actions plan ${ProgramType} ${fyear}.xlsx`
    case 'ExportPartner360TargetsComparison':
      return !fyear
        ? 'partner 360 actuals targets.xlsx'
        : `partner 360 actuals targets ${fyear}.xlsx`
    case 'ExportMasterDealerReport':
      return `Master Dealer Data ${pid}.xlsx`
    default:
      return !fyear ? `${pid}.xlsx` : `${pid}_FY_${fyear}.xlsx`
  }
}

const getKeys = (
  type: ExportMappingTypes,
  keys: string[],
  pid?: string,
  fyear?: number,
  programType?: string,
  partnerType?: string,
  partnerRank?: number,
  opsPartner?: number,
  bpsPartner?: number,
  strategicMPSPartner?: number,
  strategicPrintServicesPartner?: number,
  strategicPSServicesPartner?: number,
  polyPartner?: number,
  amplifyImpact?: number
) => {
  switch (type) {
    case ExportMappingTypes.ExportMasterDealerReport: {
      const keysObj1 = {}
      keysObj1['MDPartnerId'] = pid
      return {
        ...getKeysFromJSON({ keys: [], pid, fyear, programType }),
        ...keysObj1,
      }
    }
    case ExportMappingTypes.ExportMDFPlan: {
      const keysObj2 = {}

      if (programType !== 'Summary') {
        keysObj2['ProgramType'] = programType
      }

      return {
        ...getKeysFromJSON({
          keys,
          pid,
          fyear,
          programType,
          partnerType,
          partnerRank,
          opsPartner,
          bpsPartner,
          strategicMPSPartner,
          strategicPrintServicesPartner,
          strategicPSServicesPartner,
          polyPartner,
          amplifyImpact,
        }),
        ...keysObj2,
      }
    }
    default:
      return getKeysFromJSON({ keys, pid, fyear, programType })
  }
}

const getKeysFromJSON = (obj: IKeysFromJSON) => {
  const keyValues = {}
  obj.keys.forEach((keyObject) => {
    let key = ''
    let field = ''
    let passEmptyValue = false
    let defaultValue: any
    // Key can be:
    // 1) String: (as "FYear" in the sample below)
    // 2) Object (with only 'key' field is mandatory)
    // "keys": ["FYear", {"key": "Market", "field": "GeoMarket", "passEmptyValue": 1}, {"key": "Country", "passEmptyValue": 1}]}]
    if (typeof keyObject === 'string') {
      key = keyObject
      field = keyObject
    } else {
      if (keyObject.key) {
        key = keyObject.key
        field = keyObject.field || keyObject.key
        passEmptyValue = !!keyObject.passEmptyValue
        defaultValue = keyObject.defaultValue
      } else {
        Logger.log(`Wrong mapping keys.`)
      }
    }
    switch (key) {
      case 'PartnerId':
        if (!!obj.pid || passEmptyValue || defaultValue) {
          keyValues[field] = obj.pid || defaultValue || ''
        }
        break
      case 'FYear':
        if (!!obj.fyear || passEmptyValue || defaultValue) {
          keyValues[field] = obj.fyear || defaultValue || 0
        }
        break
      case 'ProgramType':
        if (!!obj.programType || passEmptyValue || defaultValue) {
          keyValues[field] = obj.programType || defaultValue || ''
        }
        break
      case 'ProgramTypes':
        if (!!obj.programType || passEmptyValue || defaultValue) {
          keyValues[field] = obj.programType || defaultValue || ''
        }
        break
      case 'PartnerType':
        if (!!obj.partnerType || passEmptyValue || defaultValue) {
          keyValues[field] = obj.partnerType || defaultValue || ''
        }
        break
      case 'PartnerRank':
        if (!!obj.partnerRank || passEmptyValue || defaultValue) {
          keyValues[field] = obj.partnerRank || defaultValue || 0
        }
        break
      case 'PartnerRanks':
        if (!!obj.partnerRanks || passEmptyValue || defaultValue) {
          keyValues[field] = obj.partnerRanks || defaultValue || ''
        }
        break
      case 'PBMEmail':
        if (!!obj.pbmEmail || passEmptyValue || defaultValue) {
          keyValues[field] = obj.pbmEmail || defaultValue || ''
        }
        break
      case 'ManagerEmail':
        if (!!obj.managerEmail || passEmptyValue || defaultValue) {
          keyValues[field] = obj.managerEmail || defaultValue || ''
        }
        break
      case 'Category':
        if (obj.category || passEmptyValue || defaultValue) {
          keyValues[field] = obj.category || defaultValue || ''
        }
        break
      case 'SubCategory':
        if (obj.subCategory || passEmptyValue || defaultValue) {
          keyValues[field] = obj.subCategory || defaultValue || ''
        }
        break
      case 'IsTopOPSPartner':
        if (!!obj.opsPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.opsPartner || defaultValue || 0
        }
        break
      case 'IsTopBPSPartner':
        if (!!obj.bpsPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.bpsPartner || defaultValue || 0
        }
        break
      case 'IsStrategicMPSPartner':
        if (!!obj.strategicMPSPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.strategicMPSPartner || defaultValue || 0
        }
        break
      case 'IsStrategicPrintServicesPartner':
        if (!!obj.strategicPrintServicesPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.strategicPrintServicesPartner || defaultValue || 0
        }
        break
      case 'IsStrategicPSServicesPartner':
        if (!!obj.strategicPSServicesPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.strategicPSServicesPartner || defaultValue || 0
        }
        break
      case 'IsPolyPartner':
        if (!!obj.polyPartner || passEmptyValue || defaultValue) {
          keyValues[field] = obj.polyPartner || defaultValue || 0
        }
        break
      case 'IsAmplifyImpact':
        if (!!obj.amplifyImpact || passEmptyValue || defaultValue) {
          keyValues[field] = obj.amplifyImpact || defaultValue || 0
        }
        break
      case 'Market':
        if (!!obj.market || passEmptyValue || defaultValue) {
          keyValues[field] = obj.market || defaultValue || ''
        }
        break
      case 'Country':
        if (!!obj.country || passEmptyValue || defaultValue) {
          keyValues[field] = obj.country || defaultValue || ''
        }
        break
      case 'ReportGUID':
        if (!!obj.guid || passEmptyValue || defaultValue) {
          keyValues[field] = obj.guid || defaultValue || ''
        }
        break
      case 'BusinessUnitPrinting':
        keyValues['BusinessUnit'] = 'Printing'
        break
      case 'BusinessUnitPersonalSystems':
        keyValues['BusinessUnit'] = 'Personal Systems'
        break
      case 'BusinessUnitSupplies':
        keyValues['BusinessUnit'] = 'Supplies'
        break
      case 'ProgramTypeKeyInitiative':
        keyValues['ProgramType'] = 'Key Initiative'
        break
      case 'ProductCategoryHardware':
        keyValues['ProductCategory'] = 'Hardware'
        break
      case 'ProductCategoryServices':
        keyValues['ProductCategory'] = 'Services'
        break
      case 'ActualsTypeNdp':
        keyValues['ActualsValueType'] = 'Ndp'
        break
      case 'ActualsTypeNet':
        keyValues['ActualsValueType'] = 'Net'
        break
      default:
        if (field && (passEmptyValue || defaultValue)) {
          keyValues[field] = defaultValue || ''
        }
        break
    }
  })
  return keyValues
}

export const generatePartnerPlanViaService = async ({
  pid,
  exportFormat,
  programType,
  fYear,
  openResultInNewTab = true,
  pdfVersion = PARTNER_PLAN_PDF_VERSION_DEFAULT,
}: IExportPartnerPlanViaService) => {
  const apiResut = await Partner360Api.generatePartnerPlan(
    pid,
    fYear,
    exportFormat,
    programType,
    exportFormat === 'PDF' ? pdfVersion : undefined
  )
  if (apiResut.status === 'success' && apiResut.data && apiResut.data.length) {
    if (openResultInNewTab) {
      const path = apiResut.data[0].Path
      const filename = apiResut.data[0].Filename
      const url = `${settings.dataServerUrl}${path}`

      const link = document.createElement('a')
      link.href = sanitizeHtml(`${url}?Guid=${guid()}`)
      if (filename) {
        link.setAttribute('download', filename)
      }
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
    return true
  }

  return false
}

export const generatePdfPartnerPlanViaAppDes = async ({
  pid,
  fYear,
  programType,
  preventDownload = false,
}: IGeneratePdfPartnerPlanViaAppDes) => {
  // Generate Partner Plan in PDF via app-designer view
  let path = `/page/partner-360-bp-report/partnerId/${pid}/fyear/${fYear}`
  path += programType && programType !== ProgramTypes.General ? `/programType/${programType}` : ''
  path += preventDownload ? `/preventDownload/${preventDownload}` : ''

  const link = document.createElement('a')
  link.href = sanitizeHtml(path)
  link.target = '_blank'
  //link.rel = 'noopener noreferrer'
  link.click()

  return true
}

export class ExportCommitmentService {
  public static exportPartnerData = async (
    type: ExportMappingTypes,
    pid?: string,
    fyear?: number,
    ProgramType?: string,
    partnerType?: string,
    partnerRank?: number,
    saveResultToAttachmentForm: boolean = false
  ) => {
    const crudOptions = CrudStore.get(CrudStoreItemPartner360.JBPSettings)
    const options = await crudOptions.getById({ id: 1, keyField: 'EntryId' })
    if (options) {
      const url = proxy(`/form/data/export/by/template`)
      Logger.log(url)
      const data = new FormData()
      let mappings = []
      try {
        mappings = JSON.parse(options[type] || '')
      } catch (e) {
        Logger.warn('error on parsing')
        Logger.error(e)
      }
      const filename = getFilename(type, pid, fyear, ProgramType)
      data.append(
        'Mappings',
        JSON.stringify(
          mappings.map((mapping: { id: string; keys: string[] }) => ({
            EntryId: parseInt(mapping.id, 10),
            Keys: getKeys(type, mapping.keys, pid, fyear, ProgramType, partnerType, partnerRank),
          }))
        )
      )
      const { app } = appGetState()
      const [token] = getTokens(app.ownerId)
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: data,
      })
      if (res && res.body) {
        if (saveResultToAttachmentForm && pid) {
          const basicInfoCrud = CrudStore.get<IFormPartnerBasicInfo>(
            CrudStoreItemPartner360.PartnerBasicInfo,
            'exported_file'
          )
          const filters: IFilter[] = [{ field: 'PartnerId', value: pid, operator: Operator.EQUAL }]
          if (fyear) {
            filters.push({ field: 'FYear', value: fyear, operator: Operator.EQUAL })
          }
          basicInfoCrud.setFilter({
            logic: Logic.AND,
            filters: filters,
          })
          basicInfoCrud.setPaging({ page: 0, pageSize: 1 })
          const result = await basicInfoCrud.fetch()
          if (result.items.length && result.items[0].EntryId) {
            const [item] = result.items
            const blob = await res.blob()
            const file = blobToFile(blob, filename)
            return await saveFile({
              formId: 621,
              formCode: 'JBPBasicInformation',
              fieldName: 'ExportedFile',
              models: [{ EntryId: item.EntryId, file }],
            })
          }
        } else {
          const blob = await res.blob()
          downloadBlob(filename, blob)
        }
      }
    }

    return ''
  }

  public static generateJBPReport = async ({
    reportType,
    pid,
    fyear,
    programType,
    market,
    country,
    category,
    subCategory,
    partnerType,
    partnerRank,
    partnerRanks,
    pbmEmail,
    managerEmail,
    opsPartner,
    bpsPartner,
    strategicMPSPartner,
    strategicPrintServicesPartner,
    strategicPSServicesPartner,
    polyPartner,
    amplifyImpact,
    saveResultToAttachmentForm = false,
    guid,
  }: IGenerateReport) => {
    ///const crudOptions = CrudStore.get(CrudStoreItemPartner360.OptionsJBP)
    //const options = await crudOptions.getById(1, false, 'EntryId')
    //if (options) {
    //const filename = getFilename(type, pid, fyear, ProgramType)
    let filename = reportType.Filename || `${pid}_FY_${fyear}.xlsx`

    filename = filename.replace('${fyear}', (fyear || 0).toString())
    filename = filename.replace('${pid}', pid || '')
    filename = filename.replace('${partnerId}', pid || '')
    filename = filename.replace('${programType}', programType || '')
    filename = filename.replace('${market}', market || '')
    filename = filename.replace('${country}', country || '')

    let mappings = []
    try {
      //mappings = JSON.parse(options[type] || '')
      mappings = JSON.parse(reportType.Mappings || '')
    } catch (e) {
      Logger.warn('error on parsing')
      Logger.error(e)
    }

    const formData = new FormData()
    formData.append(
      'Mappings',
      JSON.stringify(
        mappings.map((mapping: { id: string; keys: string[] }) => ({
          EntryId: parseInt(mapping.id, 10),
          Keys: getKeysFromJSON({
            keys: mapping.keys,
            pid,
            fyear,
            programType,
            partnerType,
            partnerRank,
            partnerRanks,
            pbmEmail,
            managerEmail,
            opsPartner,
            bpsPartner,
            strategicMPSPartner,
            strategicPrintServicesPartner,
            strategicPSServicesPartner,
            polyPartner,
            amplifyImpact,
            market,
            country,
            category,
            subCategory,
            guid,
          }),
        }))
      )
    )

    const { app } = appGetState()
    const [token] = getTokens(app.ownerId)
    const url = proxy(`/form/data/export/by/template`)

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: formData,
    })

    if (response.status.toString().startsWith('2')) {
      if (saveResultToAttachmentForm && pid && fyear) {
        const basicInfoCrud = CrudStore.get<IFormPartnerBasicInfo>(
          CrudStoreItemPartner360.PartnerBasicInfo,
          'exported_file'
        )
        basicInfoCrud.setFilter({
          logic: Logic.AND,
          filters: [
            { field: 'PartnerId', value: pid, operator: Operator.EQUAL },
            { field: 'FYear', value: fyear, operator: Operator.EQUAL },
          ],
        })
        basicInfoCrud.setPaging({ page: 0, pageSize: 1 })
        const result = await basicInfoCrud.fetch()
        if (result.items.length && result.items[0].EntryId) {
          const [item] = result.items
          const blob = await response.blob()
          const file = blobToFile(blob, filename)
          return await saveFile({
            formId: 621,
            formCode: 'JBPBasicInformation',
            fieldName: 'ExportedFile',
            models: [{ EntryId: item.EntryId, file }],
          })
        }
      } else {
        const blob = await response.blob()
        downloadBlob(filename, blob)
      }
    } else {
      NotificationController.showError({
        message: 'There was some unexpected problems. Try again later.',
      })
    }

    return
  }

  public static generateJBPReportNew = async ({
    reportType,
    pid,
    fyear,
    programType,
    market,
    country,
    category,
    subCategory,
    scope,
    products,
    globalPartnerName,
    partnerType,
    partnerRank,
    partnerRanks,
    pbmEmail,
    managerEmail,
    opsPartner,
    bpsPartner,
    strategicMPSPartner,
    strategicPrintServicesPartner,
    strategicPSServicesPartner,
    polyPartner,
    amplifyImpact,
    saveResultToAttachmentForm = false,
    guid,
  }: IGenerateReport) => {
    const formattedFY = `FY${(fyear || 0).toString().slice(-2) || 'XX'}`

    const partnerScope =
      (() => {
        if (scope === 'ManualGlobal') {
          return globalPartnerName
            ? globalPartnerName[0].CommunicationName || globalPartnerName[0].PartnerID
            : ''
        }
        if (scope === 'All') {
          return partnerType
        }
        return scope
      })() || ''

    const formattedProducts = (products || []).length > 0 ? products!.join(', ') : 'All Categories'

    let filename = reportType.Filename || `${pid}_FY_${fyear}.xlsx`

    filename = filename.replace('${fyear}', (fyear || 0).toString())
    filename = filename.replace('${formattedFY}', formattedFY)
    filename = filename.replace('${pid}', pid || '')
    filename = filename.replace('${partnerId}', pid || '')
    filename = filename.replace('${programType}', programType || '')
    filename = filename.replace('${market}', market || '')
    filename = filename.replace('${country}', country || '')
    filename = filename.replace('${partnerScope}', scope || '')
    filename = filename.replace('${products}', formattedProducts)
    filename = filename.replace('${partnerType}', partnerScope)

    let mappings = []
    try {
      //mappings = JSON.parse(options[type] || '')
      mappings = JSON.parse(reportType.Mappings || '')
    } catch (e) {
      Logger.warn('error on parsing')
      Logger.error(e)
    }

    const payload = {
      mappings: JSON.stringify(
        mappings.map((mapping: { id: string; keys: string[] }) => ({
          EntryId: parseInt(mapping.id, 10),
          Keys: getKeysFromJSON({
            keys: mapping.keys,
            pid,
            fyear,
            programType,
            partnerType,
            partnerRank,
            partnerRanks,
            pbmEmail,
            managerEmail,
            opsPartner,
            bpsPartner,
            strategicMPSPartner,
            strategicPrintServicesPartner,
            strategicPSServicesPartner,
            polyPartner,
            amplifyImpact,
            market,
            country,
            category,
            subCategory,
            guid,
          }),
        }))
      ),
      filename: filename,
      attachment: null,
    }

    // eslint-disable-next-line no-restricted-syntax
    const isParameterExist = import.meta.env.VITE_HTTPS_PARTNER_360
      ? // eslint-disable-next-line no-restricted-syntax
        import.meta.env.VITE_HTTPS_PARTNER_360
      : 'https://partner360-service-itg.corp.hpicloud.net'

    const { app } = appGetState()
    const [token] = getTokens(app.ownerId)
    const url = `${isParameterExist}/api/v0/exportDocument/download`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify(payload),
    })

    if (response.status.toString().startsWith('2')) {
      if (saveResultToAttachmentForm && pid && fyear) {
        const basicInfoCrud = CrudStore.get<IFormPartnerBasicInfo>(
          CrudStoreItemPartner360.PartnerBasicInfo,
          'exported_file'
        )
        basicInfoCrud.setFilter({
          logic: Logic.AND,
          filters: [
            { field: 'PartnerId', value: pid, operator: Operator.EQUAL },
            { field: 'FYear', value: fyear, operator: Operator.EQUAL },
          ],
        })
        basicInfoCrud.setPaging({ page: 0, pageSize: 1 })
        const result = await basicInfoCrud.fetch()
        if (result.items.length && result.items[0].EntryId) {
          const [item] = result.items
          const blob = await response.blob()
          const file = blobToFile(blob, filename)
          return await saveFile({
            formId: 621,
            formCode: 'JBPBasicInformation',
            fieldName: 'ExportedFile',
            models: [{ EntryId: item.EntryId, file }],
          })
        }
      } else {
        const blob = await response.blob()
        downloadBlob(filename, blob)
      }
    } else {
      NotificationController.showError({
        message: 'There was some unexpected problems. Try again later.',
      })
    }

    return
  }
}

// @ts-ignore
const blobToFile = (theBlob: Blob, fileName: string): File => {
  const b: any = theBlob
  b.lastModifiedDate = new Date()
  b.name = fileName
  return theBlob as File
}
